import { useEffect, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import "../../components/phoneLibrary/phoneLibrary.css";
import { PHONE_DEFAULT_VALUE } from "../../helpers/constants";
import {
  getHelperText,
  getValueOrBlank,
  isEmpty,
  isNotEmpty,
} from "../../helpers/utils";
import { ErrorCrossIcon } from "../errorCrossIcon/ErrorCrossIcon";
import PhoneInput from "../phoneLibrary";
import { MuiTypography } from "../typography/MuiTypography";
import { MuiSelectPhoneWithTextInputWrapper } from "./MuiSelectPhoneWithTextInput.style";

export interface PhoneInputOnChangeProps {
  phoneNumberValue: string;
  countryDetails: {
    name: string;
    dialCode: string;
    countryCode: string;
    format: string;
  };
}
interface MuiSelectPhoneWithTextInputProps {
  label?: string;
  disabled?: boolean;
  isMandatory?: boolean;
  placeholder?: string;
  defaultSelectedCountry?: string;
  onChange?: (
    phoneNumberValue: PhoneInputOnChangeProps["phoneNumberValue"],
    countryDetails: PhoneInputOnChangeProps["countryDetails"],
    formattedValue: string,
  ) => void;
  countryIsoCodeFormId: string;
  phoneNumberFormId: string;
  countryCodeFormId: string;
  isEdit?: boolean;
  ignoreError?: boolean;
  reset?: boolean;
  defaultValue?: string;
  withSelectOption?: boolean;
  searchPlaceholder: string;
  searchNotFound: string;
  selectLabel: string;
  invalidPhoneNumberMessage: string;
  makeFocus?: boolean;
  hideFormattingAndDisableValidation?: boolean;
}

// min length is 8 + countryCode(49) + prefix(+) + space( ) => +XX XXXXXXXX (for Germany it's 12 with other chars and space)
const GERMANY_MIN_PHONE_NUMBER_LENGTH_WITH_SPACE_AND_OTHER_CHARS = 12;
// max length is 15 + countryCode(49) + prefix(+) + space( ) => +XX XXXXXXXXXXXXX (for Germany it's 19 with other chars and space)
const GERMANY_MAX_PHONE_NUMBER_LENGTH_WITH_SPACE_AND_OTHER_CHARS = 19;
const GERMANY_DIAL_CODE = "49";
const MIN_LENGTH_FOR_EDIT_MODE = 3;
const MAX_PHONE_NUMBER_LENGTH_EDIT_MODE = 20;

export const MuiSelectPhoneWithTextInput: React.FC<
  MuiSelectPhoneWithTextInputProps
> = ({
  label,
  disabled,
  isMandatory,
  onChange,
  placeholder,
  defaultSelectedCountry = PHONE_DEFAULT_VALUE.DEFAULT_ISO_CODE,
  countryIsoCodeFormId,
  phoneNumberFormId,
  countryCodeFormId,
  ignoreError = false,
  reset = false,
  defaultValue = "",
  withSelectOption = false,
  searchPlaceholder,
  searchNotFound,
  selectLabel,
  invalidPhoneNumberMessage,
  makeFocus = false,
  hideFormattingAndDisableValidation,
}) => {
  const {
    control,
    setError,
    setValue,
    clearErrors,
    formState: { errors },
  } = useFormContext();

  const errorRef = useRef(false);
  const prefix = "+";
  const [phoneValue, setPhoneValue] = useState("");

  useEffect(() => {
    if (defaultValue) {
      setPhoneValue(defaultValue);
    }
  }, [defaultValue]);

  useEffect(() => {
    if (reset) {
      setPhoneValue("");
    }
  }, [reset]);

  const validatePhone = (
    number: string,
    countryDetails: PhoneInputOnChangeProps["countryDetails"],
  ) => {
    // Example -
    // number - +1 (123) 456-7890
    // format - +. (...) ...-....
    if (number === undefined) {
      return;
    }
    const phoneNumber = number;
    number = `${prefix}${countryDetails.dialCode} ${number}`;
    if (countryDetails.dialCode === GERMANY_DIAL_CODE) {
      const isValid =
        number.length >=
          GERMANY_MIN_PHONE_NUMBER_LENGTH_WITH_SPACE_AND_OTHER_CHARS &&
        number.length <=
          GERMANY_MAX_PHONE_NUMBER_LENGTH_WITH_SPACE_AND_OTHER_CHARS;
      return isValid;
    }
    if (hideFormattingAndDisableValidation) {
      const isValid = phoneNumber.length >= MIN_LENGTH_FOR_EDIT_MODE;
      return isValid;
    }
    const isValid = number.length === countryDetails.format.length;
    return isValid;
  };

  const handleInputPhoneNumberChange = (
    phoneNumberValue: PhoneInputOnChangeProps["phoneNumberValue"],
    countryDetails: PhoneInputOnChangeProps["countryDetails"],
    _e: React.ChangeEvent<HTMLInputElement>,
    formattedValue: string,
  ) => {
    if (
      countryDetails.countryCode?.toLowerCase() ===
      PHONE_DEFAULT_VALUE.DEFAULT_SELECT_CODE
    ) {
      setValue(countryIsoCodeFormId, "");
      setValue(countryCodeFormId, "");
    } else {
      setValue(
        countryIsoCodeFormId,
        getValueOrBlank(countryDetails?.countryCode?.toLowerCase()),
      );
      setValue(countryCodeFormId, `${prefix}${countryDetails.dialCode}`);
    }

    if (isEmpty(formattedValue)) {
      setValue(phoneNumberFormId, "", {
        shouldValidate: true,
        shouldDirty: true,
      });
      // have to add here otherwise it won't update last digit
      errorRef.current = false;
      return;
    }
    const isValidNumber = validatePhone(formattedValue, countryDetails);
    if (isValidNumber) {
      setValue(phoneNumberFormId, formattedValue, {
        shouldValidate: true,
        shouldDirty: true,
      });
      errorRef.current = false;
    } else {
      errorRef.current = true;
    }
    onChange?.(phoneNumberValue, countryDetails, formattedValue);
  };

  const getErrorMessage = () => {
    const rawError =
      (errors[phoneNumberFormId]?.message as unknown as string) ?? "";

    return getHelperText(rawError, label ?? "");
  };

  const hasError = () => {
    return isNotEmpty(errors) && isNotEmpty(errors[phoneNumberFormId]);
  };

  const handleInputOnBlur = (
    _e: React.FocusEvent<HTMLInputElement>,
    _countryData: PhoneInputOnChangeProps["countryDetails"],
  ) => {
    const phoneNumberFormError = errors[phoneNumberFormId];
    if (errorRef.current) {
      !ignoreError &&
        setError(phoneNumberFormId, {
          type: "custom",
          message: invalidPhoneNumberMessage,
        });
    } else if (
      hasError() &&
      typeof phoneNumberFormError?.type === "string" &&
      phoneNumberFormError.type.startsWith("custom")
    ) {
      clearErrors(phoneNumberFormId);
    }
  };

  return (
    <MuiSelectPhoneWithTextInputWrapper>
      {label && (
        <MuiTypography
          className={disabled ? "mui-label disabled" : "mui-label"}
          component="label"
          variant="h3"
        >
          {label} {isMandatory && <label className="asterisk">*</label>}
        </MuiTypography>
      )}

      <PhoneInput
        specialLabel={""}
        disableCountryGuess
        disableCountryCode
        enableSearch
        control={control}
        value={phoneValue}
        containerClass={`${hasError() ? "errorState-border" : ""}`}
        country={defaultSelectedCountry?.toLowerCase()}
        placeholder={placeholder}
        onChange={handleInputPhoneNumberChange}
        onBlur={handleInputOnBlur}
        clearValue={reset}
        withSelectOption={withSelectOption}
        searchPlaceholder={searchPlaceholder}
        searchNotFound={searchNotFound}
        selectLabel={selectLabel}
        makeFocus={makeFocus}
        alwaysDefaultMask={hideFormattingAndDisableValidation}
        {...(hideFormattingAndDisableValidation && {
          enableLongNumbers: MAX_PHONE_NUMBER_LENGTH_EDIT_MODE,
        })}
      />

      {hasError() && (
        <MuiTypography variant="h5SemiBold" className="errorState">
          <ErrorCrossIcon /> {getErrorMessage()}
        </MuiTypography>
      )}
    </MuiSelectPhoneWithTextInputWrapper>
  );
};
